import * as React from "react"
import {clsx} from "clsx"
import styles from "./PasswordRequirements.module.css"

interface PasswordRequirement {
	/** Stable key for the rule (e.g. `"minimumLength"`). */
	id: string
	/** Human-readable, already-localised requirement text. */
	label: React.ReactNode
	/** Whether the current password satisfies this rule. */
	met: boolean
}

interface PasswordRequirementsProps extends React.ComponentPropsWithoutRef<"ul"> {
	requirements: PasswordRequirement[]
}

/**
 * The met/unmet checklist that accompanies a password field, authored in the Base
 * UI style. Presentation only — pass it a computed list (see `usePasswordRequirements`)
 * and it renders each rule with a check (met) or cross (unmet). Complements
 * `PasswordStrengthMeter`: the meter rates entropy, this reflects policy compliance.
 */
const PasswordRequirements = React.forwardRef<HTMLUListElement, PasswordRequirementsProps>(
	function PasswordRequirements({requirements, className, ...props}, ref) {
		return (
			<ul
				ref={ref}
				data-slot="password-requirements"
				className={clsx(styles.root, className)}
				{...props}
			>
				{requirements.map((requirement) => (
					<li
						key={requirement.id}
						data-slot="password-requirement"
						data-met={requirement.met ? "" : undefined}
						className={styles.item}
						role="checkbox"
						aria-checked={requirement.met}
						aria-readonly="true"
					>
						<span className={styles.icon} aria-hidden="true"/>
						<span className={styles.text}>{requirement.label}</span>
					</li>
				))}
			</ul>
		)
	},
)

export {PasswordRequirements}
export type {PasswordRequirement, PasswordRequirementsProps}
